import type { DocumentActivityEvent } from "@magic-md/engine-client/protocol";
import type { StateCreator } from "zustand";
import { scheduleSessionPersist } from "./tabs-slice";
import {
    isPersistablePreviewWidthSetting,
    isPreviewWidthValue,
    normalizePreviewWidth,
} from "../../lib/preview-width";
import type { EditorState, PreviewWidth, SidebarView, ViewSlice } from "./types";
import {
    pinnedUnitSessionRegistry,
    releasePinnedUnitSession,
} from "../pinned-unit-sessions";
import {
    getSidebarPresentation,
    persistEditorUserSetting,
    splitPercentForViewMode,
    viewModeForSplitPercent,
} from "./utils";

export const createViewSlice: StateCreator<EditorState, [], [], ViewSlice> = (
    set,
    get,
) => ({
    defaultViewMode: "editor",
    viewMode: "editor",
    splitPercent: 100,
    sidebarOpen: false,
    sidebarView: "explorer",
    syntaxHoverDetailEnabled: true,
    codeMirrorWidgetMode: "pretty",
    autoSaveEnabled: false,
    showInlineDiagnosticsEnabled: true,

    rightSidebarOpen: false,
    rightSidebarDefinitionId: null,
    rightSidebarDefinitionLocator: null,
    rightSidebarDefinitionTarget: null,
    inlineRenderedDefinitionIds: [],
    pinnedUnits: [],
    activePinnedUnitId: null,

    snapshotColumnWidth: 320, // default width in pixels

    snapshotPreviewState: {
        enabled: false,
        filePath: null,
        currentSnapshotId: null,
        comparisonMode: "content",
    },

    selectedPluginName: null,
    selectedInstanceId: null,
    selectedSidecarId: null,
    selectedExtensionId: null,
    showEngineDetail: false,
    showMcpDetail: false,
    showSessionDetail: false,

    mcpStatus: "stopped",
    mcpEndpoint: null,
    mcpPort: null,
    mcpConnectedClients: 0,
    mcpClientSessions: [],
    mcpKnownClientSessions: [],
    mcpRequestLogs: [],
    mcpTools: [],
    mcpPrompts: [],
    mcpResources: [],

    documentActivities: new Map(),

    previewWidth: "fill",

    statusMessage: null,

    /* ── View ──
     *
     * Convergence invariant (Phase 3 #20):
     *   - When `panes` is empty (single-pane mode), global `viewMode`/
     *     `splitPercent` is the canonical source of truth.
     *   - When `panes` is non-empty (multi-pane mode), each pane carries its
     *     own viewMode AND the focused pane's viewMode is mirrored into the
     *     global `viewMode`/`splitPercent` fields so that selectors like
     *     `useEditorStore(s => s.viewMode)` remain consistent.
     *
     * `setViewMode` therefore updates BOTH the global field and the focused
     * pane (if any). The matching mirror logic for the per-pane mutators
     * lives in `pane-slice.ts`.
     */
    setViewMode: (viewMode) => {
        const pct = splitPercentForViewMode(viewMode);
        set((s) => {
            if (s.panes.length === 0) {
                return { viewMode, splitPercent: pct };
            }
            const focusedId = s.focusedPaneId;
            const nextPanes = s.panes.map((p) =>
                p.paneId === focusedId
                    ? { ...p, viewMode, splitPercent: pct }
                    : p,
            );
            return { viewMode, splitPercent: pct, panes: nextPanes };
        });
        scheduleSessionPersist(get);
    },
    setSplitPercent: (pct) => {
        const clamped = Math.max(0, Math.min(100, pct));
        const viewMode = viewModeForSplitPercent(clamped);
        set((s) => {
            if (s.panes.length === 0) {
                return { splitPercent: clamped, viewMode };
            }
            const focusedId = s.focusedPaneId;
            const nextPanes = s.panes.map((p) =>
                p.paneId === focusedId
                    ? { ...p, splitPercent: clamped, viewMode }
                    : p,
            );
            return { splitPercent: clamped, viewMode, panes: nextPanes };
        });
        scheduleSessionPersist(get);
    },
    toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
    toggleRightSidebar: () =>
        set((s) => ({
            rightSidebarOpen: !s.rightSidebarOpen,
            rightSidebarDefinitionId: s.rightSidebarOpen
                ? null
                : s.rightSidebarDefinitionId,
            rightSidebarDefinitionLocator: s.rightSidebarOpen
                ? null
                : s.rightSidebarDefinitionLocator,
            rightSidebarDefinitionTarget: s.rightSidebarOpen
                ? null
                : s.rightSidebarDefinitionTarget,
        })),
    setRightSidebarDefinitionId: (id, locator, target) => set({
        rightSidebarDefinitionId: id,
        rightSidebarDefinitionLocator: id ? (locator ?? null) : null,
        rightSidebarDefinitionTarget: id && locator ? (target ?? null) : null,
    }),
    addPinnedUnit: (unit, options) => {
        pinnedUnitSessionRegistry.retain(unit, options?.sessionId);
        set((s) => {
            const existingIndex = s.pinnedUnits.findIndex((item) => item.id === unit.id);
            const nextUnit = {
                ...unit,
                view: unit.view ?? { mode: "docked" as const },
            };
            const pinnedUnits = existingIndex >= 0
                ? s.pinnedUnits.map((item, index) =>
                    index === existingIndex ? { ...item, ...nextUnit } : item,
                )
                : [...s.pinnedUnits, nextUnit];
            return {
                pinnedUnits,
                activePinnedUnitId: nextUnit.id,
                sidebarOpen: true,
            };
        });
        scheduleSessionPersist(get);
    },
    removePinnedUnit: (id) => {
        releasePinnedUnitSession(id);
        set((s) => ({
            pinnedUnits: s.pinnedUnits.filter((unit) => unit.id !== id),
            activePinnedUnitId:
                s.activePinnedUnitId === id ? null : s.activePinnedUnitId,
        }));
        scheduleSessionPersist(get);
    },
    setPinnedUnits: (units) => {
        pinnedUnitSessionRegistry.replaceAll(units);
        set((s) => ({
            pinnedUnits: units,
            activePinnedUnitId: units.some((unit) => unit.id === s.activePinnedUnitId)
                ? s.activePinnedUnitId
                : null,
        }));
        scheduleSessionPersist(get);
    },
    showPinnedUnit: (id) =>
        set((s) => {
            const exists = s.pinnedUnits.some((unit) => unit.id === id);
            if (!exists) return s;
            return {
                activePinnedUnitId: id,
                sidebarOpen: true,
            };
        }),
    closePinnedUnitPanel: () => set({ activePinnedUnitId: null }),
    floatPinnedUnit: (id, geometry) => {
        set((s) => ({
            pinnedUnits: s.pinnedUnits.map((unit) =>
                unit.id === id
                    ? {
                        ...unit,
                        view: {
                            mode: "floating",
                            ...geometry,
                        },
                    }
                    : unit,
            ),
            activePinnedUnitId: s.activePinnedUnitId === id ? null : s.activePinnedUnitId,
            sidebarOpen: s.activePinnedUnitId === id ? false : s.sidebarOpen,
        }));
        scheduleSessionPersist(get);
    },
    dockPinnedUnit: (id) => {
        set((s) => ({
            pinnedUnits: s.pinnedUnits.map((unit) =>
                unit.id === id
                    ? {
                        ...unit,
                        view: {
                            ...unit.view,
                            mode: "docked",
                        },
                    }
                    : unit,
            ),
            activePinnedUnitId: id,
            sidebarOpen: true,
        }));
        scheduleSessionPersist(get);
    },
    closeFloatingPinnedUnit: (id) => {
        set((s) => ({
            pinnedUnits: s.pinnedUnits.map((unit) =>
                unit.id === id
                    ? {
                        ...unit,
                        view: {
                            ...unit.view,
                            mode: "docked",
                        },
                    }
                    : unit,
            ),
            activePinnedUnitId:
                s.activePinnedUnitId === id ? null : s.activePinnedUnitId,
        }));
        scheduleSessionPersist(get);
    },
    moveFloatingPinnedUnit: (id, x, y) => {
        set((s) => ({
            pinnedUnits: s.pinnedUnits.map((unit) =>
                unit.id === id
                    ? {
                        ...unit,
                        view: {
                            ...unit.view,
                            mode: "floating",
                            x,
                            y,
                        },
                    }
                    : unit,
            ),
        }));
        scheduleSessionPersist(get);
    },
    resizeFloatingPinnedUnit: (id, width, height) => {
        set((s) => ({
            pinnedUnits: s.pinnedUnits.map((unit) =>
                unit.id === id
                    ? {
                        ...unit,
                        view: {
                            ...unit.view,
                            mode: "floating",
                            width,
                            height,
                        },
                    }
                    : unit,
            ),
        }));
        scheduleSessionPersist(get);
    },
    toggleInlineRenderedDefinition: (id) =>
        set((s) => {
            const isRendered = s.inlineRenderedDefinitionIds.includes(id);
            return {
                inlineRenderedDefinitionIds: isRendered
                    ? s.inlineRenderedDefinitionIds.filter((x) => x !== id)
                    : [...s.inlineRenderedDefinitionIds, id],
            };
        }),
    clearInlineRenderedDefinitions: () => set({ inlineRenderedDefinitionIds: [] }),
    setSelectedPluginName: (name) =>
        set({
            selectedPluginName: name,
            selectedSidecarId: null,
            selectedExtensionId: null,
            showEngineDetail: false,
            showMcpDetail: false,
            showSessionDetail: false,
        }),
    setSelectedInstanceId: (id) => set({ selectedInstanceId: id }),
    setSelectedSidecarId: (id) =>
        set({
            selectedSidecarId: id,
            selectedPluginName: null,
            selectedExtensionId: null,
            showEngineDetail: false,
            showMcpDetail: false,
            showSessionDetail: false,
        }),
    setSelectedExtensionId: (id) =>
        set({
            selectedExtensionId: id,
            selectedPluginName: null,
            selectedSidecarId: null,
            showEngineDetail: false,
            showMcpDetail: false,
            showSessionDetail: false,
        }),
    setShowEngineDetail: (show) =>
        set({
            showEngineDetail: show,
            selectedSidecarId: null,
            selectedPluginName: null,
            selectedExtensionId: null,
            showMcpDetail: false,
            showSessionDetail: false,
        }),
    setShowMcpDetail: (show) =>
        set({
            showMcpDetail: show,
            showEngineDetail: false,
            selectedSidecarId: null,
            selectedPluginName: null,
            selectedExtensionId: null,
            showSessionDetail: false,
        }),
    setShowSessionDetail: (show) =>
        set({
            showSessionDetail: show,
            showEngineDetail: false,
            showMcpDetail: false,
            selectedSidecarId: null,
            selectedPluginName: null,
            selectedExtensionId: null,
        }),
    closeAllDetailViews: () =>
        set({
            selectedPluginName: null,
            selectedInstanceId: null,
            selectedSidecarId: null,
            selectedExtensionId: null,
            showEngineDetail: false,
            showMcpDetail: false,
            showSessionDetail: false,
        }),
    setMcpStatus: (
        status,
        endpoint,
        port,
        connectedClients,
        clients,
        tools,
        prompts,
        resources,
        knownClients,
    ) =>
        set((s) => ({
            mcpStatus: status,
            mcpEndpoint: endpoint ?? null,
            mcpPort: port ?? null,
            mcpConnectedClients: connectedClients ?? 0,
            mcpClientSessions: clients ?? [],
            mcpKnownClientSessions: knownClients ?? clients ?? s.mcpKnownClientSessions,
            mcpTools: tools ?? s.mcpTools,
            mcpPrompts: prompts ?? s.mcpPrompts,
            mcpResources: resources ?? s.mcpResources,
        })),
    pushMcpRequestLog: (entry) => {
        const MAX_LOGS = 500;
        set((s) => {
            const logs = [...s.mcpRequestLogs];
            const idx = logs.findIndex((l) => l.id === entry.id);
            if (idx >= 0) {
                logs[idx] = entry;
            } else {
                logs.push(entry);
                if (logs.length > MAX_LOGS) {
                    logs.splice(0, logs.length - MAX_LOGS);
                }
            }
            return { mcpRequestLogs: logs };
        });
    },
    clearMcpRequestLogs: () => set({ mcpRequestLogs: [] }),
    handleDocumentActivity: (event: DocumentActivityEvent) => {
        const FADE_DELAY_MS = 5_000;
        const { operation, sessionId } = event;
        set((s) => {
            const next = new Map(s.documentActivities);
            const existing = next.get(sessionId) ?? [];
            const sourceKey = `${event.source.type}:${event.source.type === "mcp" ? event.source.sessionId : ""}`;
            const filtered = existing.filter(
                (e) =>
                    `${e.source.type}:${e.source.type === "mcp" ? e.source.sessionId : ""}` !==
                    sourceKey,
            );
            next.set(sessionId, [...filtered, event]);
            return { documentActivities: next };
        });
        if (
            operation === "completed" ||
            operation === "failed" ||
            operation === "write" ||
            operation === "read"
        ) {
            setTimeout(() => {
                set((s) => {
                    const next = new Map(s.documentActivities);
                    const existing = next.get(sessionId) ?? [];
                    const filtered = existing.filter((e) => e !== event);
                    if (filtered.length === 0) {
                        next.delete(sessionId);
                    } else {
                        next.set(sessionId, filtered);
                    }
                    return { documentActivities: next };
                });
            }, FADE_DELAY_MS);
        }
    },
    setPreviewWidth: (width: PreviewWidth) => {
        const nextWidth = normalizePreviewWidth(width);
        set({ previewWidth: nextWidth });
        if (isPersistablePreviewWidthSetting(nextWidth)) {
            void persistEditorUserSetting("editor.defaultPreviewWidth", nextWidth);
        }
        scheduleSessionPersist(get);
    },
    setSidebarView: (view: SidebarView) => {
        const { sidebarOpen, sidebarView } = get();
        if (sidebarOpen && sidebarView === view) {
            set({ sidebarOpen: false });
        } else {
            set({ sidebarOpen: true, sidebarView: view });
        }
        if (view !== "engine") {
            set({ selectedPluginName: null });
            set({ selectedSidecarId: null });
            set({ selectedExtensionId: null });
            set({ showEngineDetail: false });
            set({ showMcpDetail: false });
            set({ showSessionDetail: false });
        }
        if (view !== "explorer") {
            set({ selectedInstanceId: null });
        }
    },
    toggleSyntaxHoverDetail: () => {
        const next = !get().syntaxHoverDetailEnabled;
        set({ syntaxHoverDetailEnabled: next });
    },
    setCodeMirrorWidgetMode: (mode) => {
        set({ codeMirrorWidgetMode: mode });
    },
    applyEditorSettings: (settings) => {
        set((state) => {
            const nextState: Partial<EditorState> = {};
            if (typeof settings.autoSave === "boolean") {
                nextState.autoSaveEnabled = settings.autoSave;
            }
            if (
                settings.defaultViewMode === "editor" ||
                settings.defaultViewMode === "split" ||
                settings.defaultViewMode === "preview"
            ) {
                nextState.defaultViewMode = settings.defaultViewMode;
                if (state.tabs.length === 0 && state.panes.length === 0) {
                    nextState.viewMode = settings.defaultViewMode;
                    nextState.splitPercent = splitPercentForViewMode(
                        settings.defaultViewMode,
                    );
                }
            }
            if (isPreviewWidthValue(settings.defaultPreviewWidth)) {
                // Only apply the default when no tabs/panes exist yet — once the
                // user is interacting, their explicit width toggles must not be
                // clobbered by a settings snapshot reapply (which can race with
                // the persist round-trip and revert the click).
                if (state.tabs.length === 0 && state.panes.length === 0) {
                    nextState.previewWidth = normalizePreviewWidth(
                        settings.defaultPreviewWidth,
                    );
                }
            }
            if (typeof settings.showInlineDiagnostics === "boolean") {
                nextState.showInlineDiagnosticsEnabled =
                    settings.showInlineDiagnostics;
            }
            if (typeof settings.codeMirrorHoverDetail === "boolean") {
                nextState.syntaxHoverDetailEnabled = settings.codeMirrorHoverDetail;
            }
            if (
                settings.codeMirrorWidgetMode === "pretty" ||
                settings.codeMirrorWidgetMode === "raw"
            ) {
                nextState.codeMirrorWidgetMode = settings.codeMirrorWidgetMode;
            }
            return nextState;
        });
    },

    setStatusMessage: (msg, autoClearMs = 4000) => {
        set({ statusMessage: msg });
        if (msg != null && autoClearMs > 0) {
            setTimeout(() => {
                if (get().statusMessage === msg) {
                    set({ statusMessage: null });
                }
            }, autoClearMs);
        }
    },

    /* Snapshot Preview Panel (Feature 5) */
    setSnapshotPreview: (filePath, snapshotId) => {
        set({
            snapshotPreviewState: {
                enabled: true,
                filePath,
                currentSnapshotId: snapshotId,
                comparisonMode: "content",
            },
        });
    },

    clearSnapshotPreview: () => {
        set({
            snapshotPreviewState: {
                enabled: false,
                filePath: null,
                currentSnapshotId: null,
                comparisonMode: "content",
            },
        });
    },

    setSnapshotComparisonMode: (mode) => {
        set((s) => ({
            snapshotPreviewState: {
                ...s.snapshotPreviewState,
                comparisonMode: mode,
            },
        }));
    },

    navigateSnapshot: (direction, snapshotList) => {
        set((s) => {
            const state = s.snapshotPreviewState;
            if (!state.enabled || !state.currentSnapshotId) return {};

            const currentIndex = snapshotList.findIndex(
                (snap) => snap.id === state.currentSnapshotId,
            );
            if (currentIndex === -1) return {};

            let nextIndex = currentIndex;
            if (direction === "prev") {
                nextIndex = Math.max(0, currentIndex - 1);
            } else if (direction === "next") {
                nextIndex = Math.min(snapshotList.length - 1, currentIndex + 1);
            }

            if (nextIndex === currentIndex) return {}; // No change

            return {
                snapshotPreviewState: {
                    ...state,
                    currentSnapshotId: snapshotList[nextIndex]?.id || null,
                },
            };
        });
    },

    setSnapshotColumnWidth: (width) => {
        set({ snapshotColumnWidth: width });
    },
});

// Re-export getSidebarPresentation so editor-store.ts can re-export it from one place.
export { getSidebarPresentation };
